import { useRef, useMemo } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SCENES } from "../../data/content";

/**
 * Dynamic Series — Tesla / Apple style pinned-scroll storytelling.
 * Four product images cross-fade as the user scrolls, each accompanied
 * by an "elevating" description (no generic scene names).
 */

export default function DynamicSeries({ onBook }) {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smooth = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 30,
    mass: 0.4,
  });

  const headerOpacity = useTransform(smooth, [0, 0.06, 0.18], [1, 1, 0]);
  const headerY = useTransform(smooth, [0, 0.2], [0, -40]);

  const n = SCENES.length;

  return (
    <section
      data-testid="dynamic-series-section"
      ref={containerRef}
      className="relative bg-[var(--gx-black)]"
      style={{ height: `${n * 110}vh` }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Cross-fading scene backgrounds (the 4 product images) */}
        <div className="absolute inset-0">
          {SCENES.map((s, i) => (
            <SceneBackground
              key={s.key}
              scene={s}
              index={i}
              total={n}
              progress={smooth}
            />
          ))}
        </div>

        {/* Vignette + grain */}
        <div className="absolute inset-0 pointer-events-none vignette" />
        <div className="absolute inset-0 pointer-events-none grain" />

        {/* Section heading — fades out as scenes begin */}
        <motion.div
          style={{ opacity: headerOpacity, y: headerY }}
          className="absolute top-0 left-0 right-0 z-30 px-5 lg:px-10 pt-24 lg:pt-32 pointer-events-none"
          data-testid="dynamic-series-heading"
        >
          <div className="max-w-[1400px] mx-auto">
            <p className="eyebrow mb-4">The Flagship · Dynamic Series</p>
            <h2 className="h1-display text-white max-w-[18ch]">
              One switch.<br />
              <span className="text-dim">Every mood of your home.</span>
            </h2>
          </div>
        </motion.div>

        {/* Scene captions — fade with their scenes */}
        <div className="absolute inset-0 z-30 flex items-end pointer-events-none">
          <div className="w-full max-w-[1400px] mx-auto px-5 lg:px-10 pb-24 lg:pb-32">
            <div className="relative h-[260px] lg:h-[300px]">
              {SCENES.map((s, i) => (
                <SceneCaption
                  key={s.key}
                  scene={s}
                  index={i}
                  total={n}
                  progress={smooth}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Progress indicator */}
        <ProgressRail total={n} progress={smooth} />

        <div className="absolute bottom-6 right-5 lg:bottom-10 lg:right-10 z-40 pointer-events-auto">
          <button
            onClick={onBook}
            data-testid="dynamic-cta"
            className="btn-gold !py-3"
          >
            Experience it live <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Sub-components ---------- */

function SceneBackground({ scene, index, total, progress }) {
  const slice = 1 / total;
  const start = index * slice;
  const end = start + slice;
  const fadeIn = Math.max(0, start - slice * 0.25);
  const fadeOut = Math.min(1, end + slice * 0.05);

  const opacity = useTransform(
    progress,
    [fadeIn, start + slice * 0.15, end - slice * 0.1, fadeOut],
    index === 0 ? [1, 1, 1, 0] : [0, 1, 1, 0],
  );
  const scale = useTransform(progress, [fadeIn, fadeOut], [1.12, 1.0]);

  return (
    <motion.div
      style={{ opacity }}
      className="absolute inset-0 will-change-[opacity]"
      data-testid={`dynamic-bg-${scene.key}`}
    >
      <motion.div
        style={{
          backgroundImage: `url(${scene.image})`,
          scale,
        }}
        className="absolute inset-0 bg-cover bg-center"
      />
      {/* Soft luxury vignette over the image */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,8,10,0.55) 0%, rgba(8,8,10,0.35) 40%, rgba(6,6,8,0.92) 100%)",
        }}
      />
    </motion.div>
  );
}

function SceneCaption({ scene, index, total, progress }) {
  const slice = 1 / total;
  const start = index * slice;
  const end = start + slice;

  const opacity = useTransform(
    progress,
    [start - slice * 0.15, start + slice * 0.18, end - slice * 0.1, end + slice * 0.05],
    [0, 1, 1, 0],
  );
  const y = useTransform(
    progress,
    [start - slice * 0.15, start + slice * 0.2, end - slice * 0.05, end + slice * 0.1],
    [40, 0, 0, -40],
  );

  return (
    <motion.div
      data-testid={`dynamic-scene-${scene.key}`}
      style={{ opacity, y }}
      className="absolute inset-0 max-w-[640px]"
    >
      <p className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-gold mb-4">
        0{index + 1} · Dynamic Series
      </p>
      <h3 className="font-display text-white text-[36px] sm:text-[48px] lg:text-[64px] leading-[1.02] tracking-[-0.03em]">
        {scene.title}.
      </h3>
      <p className="lead mt-5 max-w-[44ch] text-white/90">{scene.sub}</p>
    </motion.div>
  );
}

function ProgressRail({ total, progress }) {
  const dots = useMemo(() => Array.from({ length: total }), [total]);
  return (
    <div className="absolute left-5 lg:left-10 top-1/2 -translate-y-1/2 z-30 hidden md:flex flex-col gap-3">
      {dots.map((_, i) => {
        const slice = 1 / total;
        const center = i * slice + slice / 2;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const height = useTransform(
          progress,
          [center - slice * 0.55, center, center + slice * 0.55],
          [10, 32, 10],
        );
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const bg = useTransform(
          progress,
          [center - slice * 0.55, center, center + slice * 0.55],
          ["rgba(255,255,255,0.18)", "#C9A961", "rgba(255,255,255,0.18)"],
        );
        return (
          <motion.span
            key={i}
            style={{ height, background: bg }}
            className="w-[2px] rounded-full block"
          />
        );
      })}
    </div>
  );
}
